// Agenda Tab Component
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  CalendarDays, User, UserCheck, Scissors, CheckCircle, Plus, ThumbsUp 
} from 'lucide-react';
import { STATUS_COLORS, STATUS_LABELS } from './constants';
import { NewAppointmentDialog } from './NewAppointmentDialog';

export function AgendaTab({ 
  todayAppointments, 
  onRefresh, 
  onMarkCompleted,
  onConfirmAppointment,
  branchId,
  barbers,
  services,
  getAuthHeaders
}) {
  const [showNewAppointment, setShowNewAppointment] = useState(false);

  const handleAppointmentCreated = () => {
    onRefresh?.();
  };

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg text-white font-heading">
          Citas de Hoy - {(() => {
            // Get date in Mexico City timezone
            const now = new Date();
            const mexicoDate = new Date(now.toLocaleString('en-US', { timeZone: 'America/Mexico_City' }));
            return mexicoDate.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });
          })()}
        </h2>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setShowNewAppointment(true)}
            size="sm"
            className="bg-gold hover:bg-gold/90 text-black"
            data-testid="new-appointment-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Nueva Cita
          </Button>
          <button 
            onClick={onRefresh}
            className="text-zinc-400 hover:text-white text-sm px-2"
          >
            Actualizar
          </button>
        </div>
      </div>
      
      {todayAppointments.length === 0 ? (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-12 text-center">
          <CalendarDays className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
          <p className="text-zinc-500 mb-4">No hay citas agendadas para hoy</p>
          <Button
            onClick={() => setShowNewAppointment(true)}
            className="bg-gold/20 hover:bg-gold/30 text-gold border border-gold/30"
          >
            <Plus className="w-4 h-4 mr-2" />
            Agendar Nueva Cita
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {todayAppointments.map((apt) => (
            <div 
              key={apt.id}
              className={`bg-zinc-900/50 border rounded-sm p-4 ${
                apt.status === 'pending' ? 'border-yellow-500/30' : 'border-zinc-800'
              }`}
              data-testid={`appointment-${apt.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="text-center min-w-[60px]">
                    <p className="text-2xl font-heading text-gold">{apt.time}</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <User className="w-4 h-4 text-zinc-500" />
                      <span className="text-white font-medium">{apt.client_name}</span>
                    </div>
                    {apt.barber_name && (
                      <div className="flex items-center gap-2 mb-1">
                        <UserCheck className="w-4 h-4 text-zinc-500" />
                        <span className="text-zinc-400 text-sm">{apt.barber_name}</span>
                      </div>
                    )}
                    {apt.service_name && (
                      <div className="flex items-center gap-2">
                        <Scissors className="w-4 h-4 text-zinc-500" />
                        <span className="text-zinc-400 text-sm">{apt.service_name}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className={`px-2 py-1 rounded-sm text-xs border ${STATUS_COLORS[apt.status]}`}>
                    {STATUS_LABELS[apt.status]}
                  </span>
                  
                  {/* Pending appointments: Show confirm button */}
                  {apt.status === 'pending' && (
                    <Button
                      size="sm"
                      onClick={() => onConfirmAppointment?.(apt.id)}
                      className="bg-blue-500/20 text-blue-500 hover:bg-blue-500/30 border border-blue-500/30"
                      data-testid={`confirm-apt-${apt.id}`}
                    >
                      <ThumbsUp className="w-3 h-3 mr-1" />
                      Confirmar
                    </Button>
                  )}
                  
                  {/* Confirmed appointments: Show complete button */}
                  {apt.status === 'confirmed' && (
                    <Button
                      size="sm"
                      onClick={() => onMarkCompleted(apt.id)}
                      className="bg-green-500/20 text-green-500 hover:bg-green-500/30"
                      data-testid={`complete-apt-${apt.id}`}
                    >
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Completar
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New Appointment Dialog */}
      <NewAppointmentDialog
        isOpen={showNewAppointment}
        onClose={() => setShowNewAppointment(false)}
        branchId={branchId}
        barbers={barbers || []}
        services={services || []}
        getAuthHeaders={getAuthHeaders}
        onSuccess={handleAppointmentCreated}
      />
    </div>
  );
}
